import type { Connector } from '@particle-network/connector-core';
import { useMemo } from 'react';
import Injected from '../assets/wallets/Injected';
import { walletConfigs, type WalletConfigProps } from '../config/wallet';
import { useConnectors } from './useConnectors';

type GetConnectorType = {
  getConnector: <properties extends Record<string, unknown> = Record<string, unknown>>() => Connector & properties;
};

export type WalletProps = {
  id: string;
  groupId?: string;
  connector: Connector;
  isInstalled?: boolean;
} & WalletConfigProps &
  GetConnectorType;

const useWalletsProps = () => {
  const connectors = useConnectors();

  const result = useMemo(
    () =>
      connectors
        .map((connector): WalletProps => {
          const rawId = connector.id.replace('solana_', '');
          const groupId = Object.keys(walletConfigs).find(
            (id) =>
              id
                .split(',')
                .map((i) => i.trim())
                .indexOf(rawId) !== -1
          );

          const props: WalletProps = {
            id: connector.id,
            name: connector.name ?? connector.id ?? connector.type,
            icon: <img src={connector.icon} alt={connector.name} width={'100%'} height={'100%'} />,
            connector,
            iconShape: 'squircle',
            isInstalled: connector.isInstalled?.(),
            getConnector: <properties extends Record<string, unknown> = Record<string, unknown>>() => {
              return connector as Connector & properties;
            },
          };

          if (groupId) {
            const wallet = walletConfigs[groupId];
            return { ...props, ...wallet, groupId };
          } else {
            if (!connector.icon) {
              props.icon = <Injected />;
            }
          }
          return props;
        })
        .filter(
          (wallet, index, self) =>
            self.findIndex((w) => w.id === wallet.id && w.connector.chainType === wallet.connector.chainType) === index
        )
        .filter((wallet, index, self) => {
          if (wallet.connector.chainType === 'solana') return true;
          const groupId = wallet.groupId;
          if (groupId && groupId.includes(',')) {
            const isMipd = wallet.connector.id.includes('.');
            if (isMipd) return true;
            const mipdIds = groupId
              .split(',')
              .map((id) => id.trim())
              .filter((id) => id.includes('.'));
            const hasMipd = self.find((w) => mipdIds.includes(w.connector.id));
            return Boolean(!hasMipd);
          }
          return true;
        })
        .sort((a, b) => {
          if (a.isInstalled && !b.isInstalled) return -1;
          if (!a.isInstalled && b.isInstalled) return 1;
          return 0;
        })
        .sort((a, b) => {
          if (a.id === 'walletConnect') return 1;
          if (b.id === 'walletConnect') return -1;
          return 0;
        }),
    [connectors]
  );

  return result;
};

export default useWalletsProps;

export const useWalletProps = (id: string): WalletProps | null => {
  const wallets = useWalletsProps();
  const wallet = wallets.find((c) => c.id === id);
  if (!wallet) return null;
  return wallet;
};
